<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const isActive = ref(true);

const uiFlags = computed(() => store.getters['lossReasons/getUIFlags']);

const nameError = computed(() => {
  if (!name.value) return '';
  if (name.value.length < 2) {
    return t('LOSS_REASON_MGMT.FORM.NAME.MINIMUM_LENGTH_ERROR');
  }
  return '';
});

const isFormValid = computed(() => {
  return name.value.length >= 2;
});

const onClose = () => {
  emit('close');
};

const addLossReason = async () => {
  if (!isFormValid.value) return;

  try {
    await store.dispatch('lossReasons/create', {
      loss_reason: {
        name: name.value,
        description: description.value,
        is_active: isActive.value,
      },
    });
    useAlert(t('LOSS_REASON_MGMT.ADD.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    const errorMessage =
      error.message || t('LOSS_REASON_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('LOSS_REASON_MGMT.ADD.TITLE')"
      :header-content="$t('LOSS_REASON_MGMT.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addLossReason">
      <woot-input
        v-model="name"
        class="w-full"
        :label="$t('LOSS_REASON_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LOSS_REASON_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="nameError"
      />

      <woot-input
        v-model="description"
        class="w-full"
        :label="$t('LOSS_REASON_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('LOSS_REASON_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <div class="flex items-center w-full gap-2 mb-4">
        <input
          id="is_active"
          v-model="isActive"
          type="checkbox"
          class="w-4 h-4"
        />
        <label for="is_active" class="text-sm text-n-slate-12">
          {{ $t('LOSS_REASON_MGMT.FORM.IS_ACTIVE.LABEL') }}
        </label>
      </div>
      <p class="text-xs text-n-slate-11 mb-4 -mt-2">
        {{ $t('LOSS_REASON_MGMT.FORM.IS_ACTIVE.HELP') }}
      </p>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('LOSS_REASON_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <Button
          type="submit"
          :label="$t('LOSS_REASON_MGMT.FORM.CREATE')"
          :disabled="!isFormValid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
